/* Lịch theo giờ trong ngày: mốc lịch hẹn / check-in theo nửa giờ, và từng người / phòng bận từ mấy giờ đến mấy giờ */
import React from "react";
import { Empty, Tooltip } from "antd";
import dayjs from "dayjs";
import type { BlockTone, TRow } from "../AppTuVan/reception";

export interface Marker { at: number; label: string; tone: "book" | "late" | "came" | "checkin" }

const TONE: Record<BlockTone, string> = {
  held: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-500/20 dark:text-amber-200 dark:border-amber-500/40",
  consult: "bg-[#3e91ab] text-white border-[#2c6b7f]",
  done: "bg-slate-200 text-slate-600 border-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600",
  exam: "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-500/20 dark:text-rose-200 dark:border-rose-500/40",
  exec: "bg-violet-500 text-white border-violet-600",
  plan: "bg-white text-violet-700 border-violet-400 border-dashed dark:bg-transparent dark:text-violet-300",
};
const TONE_LABEL: Record<BlockTone, string> = {
  held: "Đã phân, chờ nhận", consult: "Đang tư vấn", done: "Đã tư vấn", exam: "Bác sĩ khám", exec: "Làm dịch vụ", plan: "Buổi hẹn làm DV",
};
const MARK: Record<Marker["tone"], string> = {
  book: "bg-slate-500", late: "bg-red-500", came: "bg-emerald-500", checkin: "bg-[#3e91ab]",
};

const SLOT = 30 * 60_000;
const hm = (t: number) => dayjs(t).format("HH:mm");

/** Gom mốc theo nửa giờ để nhiều khách cùng giờ không đè nhau */
function buckets(ms: Marker[]) {
  const map = new Map<number, Marker[]>();
  ms.forEach((m) => {
    const k = Math.floor(m.at / SLOT) * SLOT;
    map.set(k, [...(map.get(k) ?? []), m]);
  });
  return [...map.entries()].map(([at, list]) => ({ at, list }));
}

export default function Timeline({ rows, booked, checkins, now, onPick }: {
  rows: TRow[];
  /** Lịch hẹn trong ngày (giờ hẹn) */
  booked: Marker[];
  /** Giờ khách check-in thật */
  checkins: Marker[];
  now: number;
  onPick: (visitId: string) => void;
}) {
  const times = [...booked, ...checkins].map((m) => m.at).concat(rows.flatMap((r) => r.blocks.flatMap((b) => [b.from, b.to])));
  const day = dayjs(now).startOf("day");
  // Khung giờ làm việc 07:00–21:00; muộn hơn thì nới phía sau, lịch trước 7h (nhập sai giờ) dồn về mép trái
  const startH = 7;
  const endH = Math.min(24, Math.max(21, ...times.filter((t) => dayjs(t).isSame(day, "day")).map((t) => dayjs(t).hour() + 1)));
  const from = day.add(startH, "hour").valueOf();
  const span = (endH - startH) * 3_600_000;
  const pct = (t: number) => `${Math.min(100, Math.max(0, ((t - from) / span) * 100))}%`;
  const hours = Array.from({ length: endH - startH + 1 }, (_, i) => startH + i);
  const nowIn = now >= from && now <= from + span;

  const grid = (
    <>
      {hours.map((h) => <span key={h} className="absolute inset-y-0 border-l border-slate-100 dark:border-slate-800" style={{ left: pct(day.add(h, "hour").valueOf()) }} />)}
      {nowIn && <span className="absolute inset-y-0 z-10 border-l-2 border-red-500" style={{ left: pct(now) }} />}
    </>
  );

  const markerRow = (label: string, sub: string, ms: Marker[]) => (
    <div className="flex items-stretch border-b border-slate-100 dark:border-slate-800">
      <div className="sticky left-0 z-20 w-40 shrink-0 bg-white py-1.5 pr-2 text-[12.5px] dark:bg-slate-900">
        <div className="font-semibold text-slate-800 dark:text-slate-100">{label}</div>
        <div className="text-[11px] text-slate-500">{sub}</div>
      </div>
      <div className="relative h-10 flex-1">
        {grid}
        {buckets(ms).map(({ at, list }) => (
          <Tooltip key={at} title={<div className="text-[12px]">{list.sort((a, b) => a.at - b.at).map((m, i) => <div key={i}>{hm(m.at)} · {m.label}</div>)}</div>}>
            <span
              className="absolute top-1/2 z-10 flex h-6 min-w-[24px] cursor-default items-center gap-0.5 rounded-full border border-white bg-white px-1 text-[11px] font-semibold shadow-sm dark:border-slate-700 dark:bg-slate-800"
              style={{ left: pct(at + SLOT / 2), transform: "translate(-50%, -50%)" }}
            >
              {list.slice(0, 4).map((m, i) => <span key={i} className={`h-2 w-2 rounded-full ${MARK[m.tone]}`} />)}
              <span className="pl-0.5 text-slate-700 dark:text-slate-200">{list.length}</span>
            </span>
          </Tooltip>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-2">
      <div className="w-full overflow-x-auto">
        <div className="min-w-[880px]">
          {/* Trục giờ */}
          <div className="flex border-b border-slate-200 dark:border-slate-700">
            <div className="sticky left-0 z-20 w-40 shrink-0 bg-white dark:bg-slate-900" />
            <div className="relative h-6 flex-1">
              {hours.map((h) => (
                <span key={h} className="absolute -translate-x-1/2 text-[11px] text-slate-500" style={{ left: pct(day.add(h, "hour").valueOf()) }}>{String(h).padStart(2, "0")}h</span>
              ))}
              {nowIn && <span className="absolute -translate-x-1/2 rounded bg-red-500 px-1 text-[10px] font-bold text-white" style={{ left: pct(now) }}>{hm(now)}</span>}
            </div>
          </div>
          {markerRow("Lịch hẹn", `${booked.length} khách đặt trước`, booked)}
          {markerRow("Check-in", `${checkins.length} khách đã đến`, checkins)}
          {rows.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có dữ liệu" />}
          {rows.map((r) => (
            <div key={r.id} className="flex items-stretch border-b border-slate-100 dark:border-slate-800">
              <div className="sticky left-0 z-20 w-40 shrink-0 bg-white py-1.5 pr-2 text-[12.5px] dark:bg-slate-900">
                <div className="truncate font-medium text-slate-800 dark:text-slate-100">{r.name}</div>
                <div className="truncate text-[11px] text-slate-500">{r.sub ?? (r.blocks.some((b) => b.from <= now && b.to >= now && b.tone !== "done" && b.tone !== "plan") ? "Đang bận" : "Đang trống")}</div>
              </div>
              <div className="relative h-10 flex-1">
                {grid}
                {r.blocks.map((b, i) => (
                  <Tooltip key={i} title={`${b.label} · ${hm(b.from)}–${hm(b.to)} · ${TONE_LABEL[b.tone]}`}>
                    <button
                      type="button"
                      onClick={() => onPick(b.visitId)}
                      className={`absolute top-1.5 z-10 h-7 overflow-hidden truncate rounded border px-1.5 text-left text-[11px] font-medium leading-6 ${TONE[b.tone]}`}
                      style={{ left: pct(b.from), width: `max(6px, calc(${pct(b.to)} - ${pct(b.from)}))` }}
                    >
                      {b.label}
                    </button>
                  </Tooltip>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11.5px] text-slate-500">
        {(Object.keys(TONE) as BlockTone[]).map((t) => <span key={t} className="flex items-center gap-1"><span className={`h-3 w-5 rounded border ${TONE[t]}`} />{TONE_LABEL[t]}</span>)}
        <span className="flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${MARK.book}`} />Chưa đến</span>
        <span className="flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${MARK.late}`} />Trễ</span>
        <span className="flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${MARK.came}`} />Đã check-in</span>
        <span className="flex items-center gap-1"><span className="h-3 border-l-2 border-red-500" />Bây giờ</span>
      </div>
    </div>
  );
}
